// ============================================================================
// TTS CATALOG (2026-06-18)
// ----------------------------------------------------------------------------
// Curated catalogue of text-to-speech models reachable through the OpenRouter
// gateway (POST /api/v1/audio/speech) + their voices. Drives the "Voice &
// Speech (TTS)" picker on the AI Models page.
//
// VOICES ARE MODEL-SPECIFIC — each model exposes its own voice names, so the
// picker is model-aware. Gender tags are honest: 'female'/'male' only where the
// provider/docs make it clear, otherwise 'other' (audition to decide). Models
// with no curated voice list fall back to a free-text voice field (leave blank
// for the model's own default).
//
// Voice names verified 2026-06-18 against OpenRouter / provider docs:
//   - Gemini female set + playground voices (Aoede, Kore, Leda, Puck, …)
//   - Grok Voice TTS: Eve, Ara, Rex, Sal, Leo (xAI / OpenRouter announcement)
//   - OpenAI gpt-4o-mini-tts standard voice set
//
// The backend does NOT import this file (Docker boundary) — it charset-validates
// the voice and passes it through to OpenRouter, which is the final arbiter. So
// adding a model/voice here is purely a frontend convenience; nothing breaks if
// a voice this catalogue doesn't know about is sent.
// ============================================================================

export const DEFAULT_TTS_MODEL = 'mistralai/voxtral-mini-tts-2603';
export const DEFAULT_TTS_VOICE = 'en_paul_sad';

// Gemini 3.1 Flash TTS — confirmed-female set first, then common others.
const GEMINI_VOICES = [
    { id: 'Aoede', label: 'Aoede', gender: 'female', note: 'Breezy' },
    { id: 'Kore', label: 'Kore', gender: 'female', note: 'Firm' },
    { id: 'Leda', label: 'Leda', gender: 'female', note: 'Youthful' },
    { id: 'Zephyr', label: 'Zephyr', gender: 'female', note: 'Bright' },
    { id: 'Autonoe', label: 'Autonoe', gender: 'female', note: 'Bright' },
    { id: 'Callirrhoe', label: 'Callirrhoe', gender: 'female', note: 'Easy-going' },
    { id: 'Despina', label: 'Despina', gender: 'female', note: 'Smooth' },
    { id: 'Erinome', label: 'Erinome', gender: 'female', note: 'Clear' },
    { id: 'Gacrux', label: 'Gacrux', gender: 'female', note: 'Mature' },
    { id: 'Laomedeia', label: 'Laomedeia', gender: 'female', note: 'Upbeat' },
    { id: 'Pulcherrima', label: 'Pulcherrima', gender: 'female', note: 'Forward' },
    { id: 'Sulafat', label: 'Sulafat', gender: 'female', note: 'Warm' },
    { id: 'Vindemiatrix', label: 'Vindemiatrix', gender: 'female', note: 'Gentle' },
    { id: 'Puck', label: 'Puck', gender: 'male', note: 'Upbeat' },
    { id: 'Charon', label: 'Charon', gender: 'male', note: 'Informative' },
    { id: 'Fenrir', label: 'Fenrir', gender: 'male', note: 'Excitable' },
    { id: 'Orus', label: 'Orus', gender: 'male', note: 'Firm' },
];

// xAI Grok Voice TTS 1.0 — 5 built-in voices.
const GROK_VOICES = [
    { id: 'Eve', label: 'Eve', gender: 'female', note: 'Warm' },
    { id: 'Ara', label: 'Ara', gender: 'female', note: 'Bright' },
    { id: 'Rex', label: 'Rex', gender: 'male', note: 'Deep' },
    { id: 'Sal', label: 'Sal', gender: 'male', note: 'Casual' },
    { id: 'Leo', label: 'Leo', gender: 'male', note: 'Confident' },
];

// OpenAI gpt-4o-mini-tts standard voices. OpenAI doesn't formally gender these;
// tags reflect common perception and are a starting point — audition to confirm.
const OPENAI_VOICES = [
    { id: 'nova', label: 'nova', gender: 'female', note: 'Warm' },
    { id: 'shimmer', label: 'shimmer', gender: 'female', note: 'Bright' },
    { id: 'coral', label: 'coral', gender: 'female', note: 'Friendly' },
    { id: 'sage', label: 'sage', gender: 'female', note: 'Calm' },
    { id: 'alloy', label: 'alloy', gender: 'other', note: 'Neutral' },
    { id: 'ash', label: 'ash', gender: 'male', note: 'Measured' },
    { id: 'ballad', label: 'ballad', gender: 'other', note: 'Expressive' },
    { id: 'echo', label: 'echo', gender: 'male', note: 'Even' },
    { id: 'fable', label: 'fable', gender: 'other', note: 'Storyteller' },
    { id: 'onyx', label: 'onyx', gender: 'male', note: 'Deep' },
    { id: 'verse', label: 'verse', gender: 'other', note: 'Lyrical' },
];

// OpenRouter's Voxtral playground uses model-specific ids such as
// `en_paul_sad`. Generic OpenAI voices (for example `alloy`) return an upstream
// 404 on this route, so keep one currently documented Voxtral voice explicit.
const VOXTRAL_TTS_VOICES = [
    { id: 'en_paul_sad', label: 'en_paul_sad', gender: 'male', note: 'OpenRouter Voxtral example' },
];

export const TTS_MODELS = [
    {
        id: 'google/gemini-3.1-flash-tts-preview',
        label: 'Google Gemini 3.1 Flash TTS',
        provider: 'Google',
        note: '30 voices · 100+ languages · speaks PCM (wrapped to WAV)',
        voices: GEMINI_VOICES,
    },
    {
        id: 'x-ai/grok-voice-tts-1.0',
        label: 'xAI Grok Voice TTS 1.0',
        provider: 'xAI',
        note: '5 voices · 20+ languages · inline speech tags',
        voices: GROK_VOICES,
    },
    {
        id: 'openai/gpt-4o-mini-tts',
        label: 'OpenAI GPT-4o mini TTS',
        provider: 'OpenAI',
        note: '11 voices · steerable tone',
        voices: OPENAI_VOICES,
    },
    {
        id: 'microsoft/mai-voice-2',
        label: 'Microsoft MAI-Voice-2',
        provider: 'Microsoft',
        note: 'Expressive SSML styles · enter a voice name',
        voices: [],
    },
    {
        id: 'mistralai/voxtral-mini-tts-2603',
        label: 'Mistral Voxtral Mini TTS',
        provider: 'Mistral',
        note: 'Multilingual · alloy default · custom voice ids supported',
        recommended: true,
        voices: VOXTRAL_TTS_VOICES,
    },
    {
        id: 'hexgrad/kokoro-82m',
        label: 'Kokoro 82M',
        provider: 'Hexgrad',
        note: 'Open-weight · enter a voice name',
        voices: [],
    },
];

/** Catalogue entry for a model id, or null if not curated. */
export function getTtsModel(modelId: any) {
    return TTS_MODELS.find(m => m.id === modelId) || null;
}

/** Voices for a model id (empty array → use the free-text voice field). */
export function getVoicesForModel(modelId: any) {
    return getTtsModel(modelId)?.voices || [];
}

/** Best default voice for a model: first female, else first listed, else '' (model default). */
export function getDefaultVoiceForModel(modelId: any) {
    const voices = getVoicesForModel(modelId);
    if (voices.length === 0) return '';
    const female = voices.find(v => v.gender === 'female');
    return (female || voices[0]!).id;
}

/**
 * Keep a stored voice only when it belongs to the selected model's curated
 * voice set. This prevents an old Gemini voice (for example `Aoede`) from
 * being sent to Voxtral after the RJ Bird privacy-safe profile is applied.
 * Models with a free-text catalogue retain the operator's custom voice id.
 */
export function resolveTtsVoiceForModel(modelId: any, storedVoice: any) {
    const model = getTtsModel(modelId);
    const voice = typeof storedVoice === 'string' ? storedVoice.trim() : '';
    if (!model) return voice;
    const voices = model.voices || [];
    if (voices.length === 0) return voice;
    if (voices.some(item => item.id === voice)) return voice;
    return getDefaultVoiceForModel(modelId);
}
